/**
 * What "Start over" and "Reset pending" say to a member (spec section 6.7,
 * screens 6 and 7).
 *
 * The two sentences of screen 7 are the SPEC'S OWN WORDS, verbatim, and they
 * are assembled here rather than written into either screen because the same
 * hold is shown in two places — the Start over route, on the device that asked,
 * and the launch notice, on every approved device — and a promise about
 * deleting a member's notes must read identically in both.
 *
 * Dates go through `dayjs` in `D MMM YYYY`, which is how the rest of the app
 * renders a date a member reads (`promise-copy.js`, `device-display.js`,
 * `voucher-expiry.js`). No new date library, and no time of day: the hold ends
 * on a day, not at a moment the member could be asked to watch for.
 *
 * A DATE THAT DOES NOT PARSE DROPS THE CLAUSE, never renders `Invalid Date`.
 * `promise-copy.js` answers the same question by rendering nothing at all,
 * which it can afford because its sentence is one line on a screen full of
 * others. Here the sentence IS the screen, and its second half — go and cancel
 * from a phone that still has them — is the actionable half and stays true
 * without a date. So the date clause is dropped and the rest is said.
 */
import dayjs from 'dayjs';
import { RESET_CONFIRMATION } from '@/utils/courses-encryption-api';

/** The same format `promise-copy.js` uses, for the same reason. */
export const RESET_DATE_FORMAT = 'D MMM YYYY';

/**
 * The phrase the member types on screen 6. NOT a literal of its own: it is the
 * exact string `POST /reset` requires as its `confirmation`
 * (`courses-encryption-api.js`, an API decision recorded in spec section 6.6),
 * so what the member types and what goes on the wire cannot drift apart.
 */
export const RESET_PHRASE = RESET_CONFIRMATION;

/**
 * Screen 7 names the device that asked, and `resetRequestedBy` is only ever a
 * device id — the API records the `X-Device-Id` header if one was sent, and
 * null if it was not. An id nobody can match to a row (a device the reset
 * itself deleted, or a caller that sent no header) is exactly this.
 */
export const UNIDENTIFIED_DEVICE = 'a device we could not identify';

/**
 * @param {string|null|undefined} iso
 * @returns {string|null} the formatted date, or null when there is none to show
 */
export function resetDate(iso) {
  if (!iso) return null;
  const at = dayjs(iso);
  return at.isValid() ? at.format(RESET_DATE_FORMAT) : null;
}

/** Screen 6: the confirmations, the phrase, and what the tap does. */
export const START_OVER_COPY = Object.freeze({
  heading: 'Start over',
  // What is actually destroyed, in the member's own vocabulary rather than the
  // schema's. The spec names no copy for screen 6 beyond "two confirmations and
  // a typed phrase"; these are the two things a member has to have understood.
  body: 'This deletes every note, highlight, tag and answer you have written in Next Steps, on every device.',
  firstConfirmation: 'I understand my notes, highlights, tags and answers will be deleted.',
  secondConfirmation: 'I understand HOGC cannot get them back for me.',
  phraseLabel: `Type ${RESET_PHRASE} to confirm`,
  phraseHint: `Exactly ${RESET_PHRASE}, in capitals.`,
  phraseCapitals: `In capitals, please: ${RESET_PHRASE}`,
  action: 'Start over',
  working: 'Starting over…',
  // The proven path, which has already happened by the time this is read.
  doneHeading: 'Your notes have been reset',
  doneBody: 'Nothing you wrote in Next Steps is left. The next time you open it, you start fresh.',
  doneAction: 'Back to Next Steps',
  // Only when the platform copy could not be removed (see reset.js): the reset
  // itself succeeded, and this is the one part of it that did not.
  doneKeyLeftBehind:
    'Your old notes key is still saved to this phone. It opens nothing, but tell us if Next Steps will not set itself up again.',
  nothingToReset: 'There is nothing to reset yet.',
});

/** Screen 7's controls, on both faces. */
export const RESET_PENDING_COPY = Object.freeze({
  heading: 'Reset pending',
  cancel: 'Cancel the reset',
  cancelling: 'Cancelling…',
  wasMe: 'This was me',
  // Only on the asked face: spec 6.7, screen 7, "with no note-taking until
  // then". On that device it is already true rather than enforced — it asked
  // unsigned, which is what a device with no key does, and the sealing hook
  // refuses every annotation write without one. Saying it beats letting the
  // member find out one refused note at a time.
  noteTakingHeld: 'You cannot write anything new in Next Steps until then.',
});

/**
 * The face shown on the device that ASKED (spec section 6.7, screen 7,
 * sentence 1 — verbatim).
 *
 * @param {{ resetAt?: string|null }|null} pendingReset `pendingReset` from
 *   `GET /api/me/encryption`, verbatim
 * @returns {string}
 */
export function askedSentence(pendingReset) {
  const on = resetDate(pendingReset?.resetAt);
  const first = on ? `Your notes will be reset on ${on}.` : 'Your notes will be reset.';
  return `${first} If you find a phone that still has them, open HOGC One there to cancel.`;
}

/**
 * The face shown on EVERY APPROVED DEVICE (spec section 6.7, screen 7,
 * sentence 2 — verbatim), which is the one the member has to see at launch.
 *
 * `deviceName` is looked up from the status' own device list by
 * `pendingReset.requestedBy`; when there is no name to show, the spec's own
 * fallback phrase stands in its place.
 *
 * @param {{ requestedAt?: string|null, resetAt?: string|null }|null} pendingReset
 * @param {string|null} [deviceName]
 * @returns {string}
 */
export function noticeSentence(pendingReset, deviceName = null) {
  // A blank name is checked here as well as in `requestingDeviceName`: a device
  // row named " " would otherwise render "requested on 5 Sep 2026 from  ." —
  // a gap where the sentence's whole point is to say where the reset came from.
  const named = typeof deviceName === 'string' && deviceName.trim() ? deviceName : null;
  const from = named || UNIDENTIFIED_DEVICE;
  const requested = resetDate(pendingReset?.requestedAt);
  const asked = requested
    ? `A reset of your notes was requested on ${requested} from ${from}.`
    : `A reset of your notes was requested from ${from}.`;
  const on = resetDate(pendingReset?.resetAt);
  const deleted = on ? `Your notes will be deleted on ${on}.` : 'Your notes will be deleted.';
  return `${asked} ${deleted}`;
}

/**
 * The name to put in the sentence above, from the status snapshot.
 *
 * Not `thisDevice`, and not the first row that happens to be APPROVED: only the
 * row whose id the API recorded as `requestedBy`. A wrong name here tells the
 * member the reset came from a phone they trust, which is the opposite of what
 * this screen is for.
 *
 * @param {{ requestedBy?: string|null }|null} pendingReset
 * @param {Array<{ id: string, name?: string }>} devices `status.devices`
 * @returns {string|null} null when there is no row to name
 */
export function requestingDeviceName(pendingReset, devices = []) {
  const id = pendingReset?.requestedBy;
  if (!id) return null;
  const row = (Array.isArray(devices) ? devices : []).find(device => device?.id === id);
  const name = row?.name;
  return typeof name === 'string' && name.trim() ? name : null;
}
